import { useRef, useState } from 'react';
import { cn } from 'cn';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { IconRail } from '@/features/reader/IconRail';
import { fetchLanguages, type Language } from '@/lib/languages';
import { THEMES, useTheme, type Theme } from '@/lib/theme';
import { useStoredPreference } from '@/features/reader/useStoredPreference';
import {
  LEADINGS,
  LEADING_LABELS,
  READER_STEPS,
  isLeading,
  isStep,
  readerStyle,
  type Leading,
  type ReaderStep,
} from '@/features/reader/readerSettings';
import {
  CHAT_FOLLOWS_READING,
  CHAT_LANGUAGE_KEY,
  LEADING_KEY,
  READING_LANGUAGE_KEY,
  STEP_KEY,
  clearPreferences,
  exportPreferences,
  importPreferences,
} from './settingsPrefs';

/**
 * Settings: the three languages, the type scale, the theme, and one honest
 * line about where all of it lives.
 *
 * Built from the kit's `SettingsForm` contract and the Library-and-Settings
 * mock. Two of its rules shape this screen more than anything else:
 *
 * - **"Three languages, told apart by what they change ... No paragraph — the
 *   example is the explanation."** Each language row carries a sample of its
 *   own effect rather than a sentence describing it.
 * - **"One line, where it matters."** No account exists, so the top of this
 *   screen says so and offers the export, rather than a sign-in prompt that
 *   would decide brief §18's open question by accident.
 *
 * **What is deliberately not here.** The contract also lists an interface
 * language, digit system and notifications. None of those have anything
 * behind them — there is no translation catalogue for the chrome, no digit
 * formatting, and no notification permission flow — and a control that
 * silently does nothing is worse than its absence. The interface row is shown
 * and disabled, with the reason, because a reader who speaks none of these
 * languages should be told the chrome is English rather than left hunting for
 * the setting. Digits and notifications are simply absent: nothing in the
 * product displays a configurable digit or sends anything.
 */

const THEME_LABELS: Record<Theme, string> = {
  system: 'Follow the system',
  light: 'Light',
  dark: 'Dark',
  paper: 'Paper',
  'high-contrast': 'High contrast',
};

/** A line of Arabic worth previewing: it carries tashkeel, which is the point. */
const PREVIEW_ARABIC = 'الْحَمْدُ لِلَّهِ رَبِّ الْعَالَمِينَ';

/**
 * A translated sample per language, used only to show what a setting changes.
 *
 * These are the SAME sentence in each language, and they are fixed strings
 * rather than khutbah content: a preview must not look like a translation the
 * product is asserting, and these never leave this screen.
 */
const PREVIEW_TRANSLATION: Record<string, string> = {
  en: 'All praise belongs to Allah, Lord of all the worlds.',
  ml: 'സർവ്വലോക രക്ഷിതാവായ അല്ലാഹുവിനാണ് സർവ്വ സ്തുതിയും.',
  kn: 'ಸಕಲ ಲೋಕಗಳ ಒಡೆಯನಾದ ಅಲ್ಲಾಹನಿಗೇ ಎಲ್ಲಾ ಸ್ತುತಿಯೂ.',
  ta: 'அனைத்துலகங்களின் இறைவனாகிய அல்லாஹ்வுக்கே எல்லாப் புகழும்.',
  ur: 'تمام تعریفیں اللہ کے لیے ہیں جو تمام جہانوں کا رب ہے۔',
  ar: PREVIEW_ARABIC,
};

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 border-b border-border py-6 last:border-b-0">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {hint ? <p className="mt-0.5 text-sm text-muted-foreground">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

/** One language setting, with a live sample of what it changes. */
function LanguageRow({
  label,
  explain,
  value,
  onChange,
  options,
  languages,
  sample,
  disabled,
  disabledReason,
}: {
  label: string;
  explain: string;
  value: string;
  onChange: (next: string) => void;
  options: readonly { code: string; label: string }[];
  languages: readonly Language[];
  /** Rendered in the chosen language's own script, as the explanation. */
  sample: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
}) {
  return (
    <div className="grid gap-3 rounded-lg border border-border p-4 sm:grid-cols-[14rem_1fr] sm:items-start">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{explain}</p>
        <div className="mt-2">
          {disabled ? (
            <p className="rounded-md border border-dashed border-border px-2.5 py-1.5 text-xs text-muted-foreground">
              {disabledReason}
            </p>
          ) : (
            <Select value={value} onValueChange={onChange}>
              <SelectTrigger aria-label={label} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => {
                  const language = languages.find((l) => l.code === option.code);
                  return (
                    <SelectItem
                      key={option.code}
                      value={option.code}
                      // One font per script, never a fallback across scripts.
                      {...(language ? { lang: language.code } : {})}
                      className={language?.scriptClass ?? undefined}
                    >
                      {option.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      {/* The example IS the explanation — the mock's rule. */}
      <div className="min-w-0 rounded-md bg-muted/40 p-3">{sample}</div>
    </div>
  );
}

export function SettingsScreen() {
  const languages = useQuery({
    // 'all', not 'target': the Arabic preview and the assistant row both
    // need a language the reader cannot choose to read in.
    queryKey: ['languages', 'all'],
    queryFn: ({ signal }) => fetchLanguages('all', signal),
    staleTime: 60 * 60 * 1000,
  });

  const { theme, setTheme } = useTheme();
  const [readingLang, setReadingLang] = useStoredPreference(
    READING_LANGUAGE_KEY,
    'ml',
    (v): v is string => typeof v === 'string' && /^[a-z]{2,3}$/.test(v),
  );
  const [chatLang, setChatLang] = useStoredPreference(
    CHAT_LANGUAGE_KEY,
    CHAT_FOLLOWS_READING,
    (v): v is string =>
      typeof v === 'string' && (v === CHAT_FOLLOWS_READING || /^[a-z]{2,3}$/.test(v)),
  );
  const [step, setStep] = useStoredPreference(STEP_KEY, '3', isStep);
  const [leading, setLeading] = useStoredPreference<Leading>(
    LEADING_KEY,
    'normal',
    isLeading,
  );

  const [notice, setNotice] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const all = languages.data ?? [];
  // Targets are what a khutbah can be READ in; `ar` is the source and is
  // never a reading language, which is why this asks the server rather than
  // listing codes.
  const targets = all.filter((l) => l.target);
  const reading = all.find((l) => l.code === readingLang);
  const resolvedChat = chatLang === CHAT_FOLLOWS_READING ? readingLang : chatLang;
  const chat = all.find((l) => l.code === resolvedChat);

  function sampleFor(language: Language | undefined, text: string | undefined) {
    if (!language || !text) {
      return <p className="text-sm text-muted-foreground">Loading…</p>;
    }
    return (
      <p
        lang={language.code}
        dir={language.direction}
        className={cn(language.scriptClass, 'text-sm text-reading-foreground')}
      >
        {text}
      </p>
    );
  }

  function download() {
    const payload = exportPreferences(new Date());
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mimbar-preferences-${payload.exportedAt.slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setNotice('Your preferences were saved to a file.');
  }

  async function upload(file: File) {
    try {
      // Untrusted input: a file the reader chose. importPreferences validates
      // every key and value before any of it reaches storage.
      const outcome = importPreferences(JSON.parse(await file.text()) as unknown);
      setNotice(
        outcome.rejected.length === 0
          ? `Restored ${outcome.applied} settings. Reload to see them everywhere.`
          : `Restored ${outcome.applied}. Ignored ${outcome.rejected.length} this version does not recognise: ${outcome.rejected.join(', ')}.`,
      );
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'That file could not be read.');
    }
  }

  return (
    <div className="flex h-[calc(100dvh-var(--app-bar-height))] overflow-hidden">
      <IconRail />
      <main className="flex min-h-0 flex-col flex-1 overflow-y-auto px-4 py-8 sm:px-6">
        <div className="mx-auto w-full max-w-3xl">
          <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>

          {/*
            The mock's "one line, where it matters". It is the truth about
            this product today: there are no accounts, so there is nowhere
            else for any of this to live.
          */}
          <div className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-4">
            <p className="text-sm">
              No account. These settings live in this browser and are lost if you
              clear it.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="lg" onClick={download}>
                Export to a file
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => fileInput.current?.click()}
              >
                Import a file
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept="application/json,.json"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void upload(file);
                  // Clear it, or choosing the same file twice fires nothing.
                  event.target.value = '';
                }}
              />
            </div>
            {notice ? (
              <p role="status" className="mt-2 text-sm text-muted-foreground">
                {notice}
              </p>
            ) : null}
          </div>

          <Section
            title="Languages"
            hint="Three settings, and each one changes something different."
          >
            <LanguageRow
              label="Reading"
              explain="The translation under the Arabic."
              value={readingLang}
              onChange={setReadingLang}
              options={targets.map((l) => ({ code: l.code, label: l.nameNative }))}
              languages={all}
              sample={
                <>
                  <p
                    lang="ar"
                    dir="rtl"
                    className="script-ar text-base text-reading-foreground"
                  >
                    {PREVIEW_ARABIC}
                  </p>
                  <div className="mt-1.5">
                    {sampleFor(reading, PREVIEW_TRANSLATION[readingLang])}
                  </div>
                </>
              }
            />

            <LanguageRow
              label="Assistant"
              explain="The language the assistant answers in."
              value={chatLang}
              onChange={setChatLang}
              options={[
                { code: CHAT_FOLLOWS_READING, label: 'Same as reading' },
                ...targets.map((l) => ({ code: l.code, label: l.nameNative })),
              ]}
              languages={all}
              sample={
                <>
                  <p className="text-xs text-muted-foreground">An answer would read:</p>
                  <div className="mt-1.5">
                    {sampleFor(chat, PREVIEW_TRANSLATION[resolvedChat])}
                  </div>
                </>
              }
            />

            <LanguageRow
              label="Interface"
              explain="The buttons and labels, like the word “Settings” itself."
              value="en"
              onChange={() => undefined}
              options={[]}
              languages={all}
              disabled
              disabledReason="English only today — the interface has not been translated yet."
              sample={
                <p className="text-sm text-muted-foreground">
                  Every label on this screen is in English, including this one.
                </p>
              }
            />
          </Section>

          <Section
            title="Reading"
            hint="Previewed in the Arabic and in your reading language, because the two scale together."
          >
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <p className="mb-1.5 text-sm font-medium">Text size</p>
                <div
                  role="group"
                  aria-label="Text size"
                  className="flex rounded-md border border-input p-0.5"
                >
                  {READER_STEPS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={String(option) === step}
                      onClick={() => setStep(String(option) as `${ReaderStep}`)}
                      className={cn(
                        'size-9 rounded-sm text-[13px]',
                        String(option) === step
                          ? 'bg-secondary text-secondary-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-sm font-medium">Line height</p>
                <div
                  role="group"
                  aria-label="Line height"
                  className="flex rounded-md border border-input p-0.5"
                >
                  {LEADINGS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={option === leading}
                      onClick={() => setLeading(option)}
                      className={cn(
                        'h-9 rounded-sm px-2.5 text-[13px]',
                        option === leading
                          ? 'bg-secondary text-secondary-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {LEADING_LABELS[option]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/*
              `reader` is the class the type scale hangs off, so the preview
              scales exactly as the reader does rather than approximating it.
              Urdu lands at Nastaliq 2.0 here for the same reason it does
              there: globals.css adds the per-script leading on top.
            */}
            <div
              className="reader rounded-lg bg-reading-surface px-5 py-4"
              style={readerStyle(Number(step) as ReaderStep, leading)}
            >
              <p lang="ar" dir="rtl" className="script-ar text-reading-foreground">
                {PREVIEW_ARABIC}
              </p>
              <div className="mt-2">
                {reading ? (
                  <p
                    lang={reading.code}
                    dir={reading.direction}
                    className={cn(reading.scriptClass, 'text-reading-foreground')}
                  >
                    {PREVIEW_TRANSLATION[readingLang] ?? ''}
                  </p>
                ) : null}
              </div>
            </div>
          </Section>

          <Section title="Appearance" hint="Applies immediately, everywhere.">
            <div
              role="group"
              aria-label="Theme"
              className="flex flex-wrap gap-2"
            >
              {THEMES.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={option === theme}
                  onClick={() => setTheme(option)}
                  className={cn(
                    'min-h-9 rounded-md border px-3 text-sm',
                    option === theme
                      ? 'border-primary bg-secondary text-secondary-foreground'
                      : 'border-input text-muted-foreground hover:text-foreground',
                  )}
                >
                  {THEME_LABELS[option]}
                </button>
              ))}
            </div>
          </Section>

          <Section
            title="This browser"
            hint="Clearing removes only what Mimbar stored here. Nothing is sent anywhere, and no khutbah is deleted."
          >
            <div>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => {
                  clearPreferences();
                  setNotice('Cleared. Reload to start from the defaults.');
                }}
              >
                Clear my settings
              </Button>
            </div>
          </Section>
        </div>
      </main>
    </div>
  );
}
